import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo/Logo';
import toast from 'react-hot-toast';
import '../Login/Auth.css';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [hasVerified, setHasVerified] = useState(false); // Prevent multiple verifications

  useEffect(() => {
    // Only verify once
    if (!hasVerified) {
      verifyEmail();
    }
  }, [token, hasVerified]);

  const verifyEmail = async () => {
    if (hasVerified) return; // Prevent duplicate calls
    
    try {
      setVerifying(true);
      setHasVerified(true); // Mark as attempted
      const response = await authAPI.verifyEmail(token);
      
      console.log('📥 Verification response:', response.data);
      
      // Check if verified or already verified
      if (response.data.verified || response.data.alreadyVerified) {
        setVerified(true);
        setError(''); // Clear any errors
        
        if (response.data.alreadyVerified) {
          toast.success('Tu correo ya estaba verificado. Puedes iniciar sesión.');
        } else {
          toast.success('¡Correo verificado exitosamente!');
        }
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Correo verificado. Ahora puedes iniciar sesión.',
              verified: true
            } 
          });
        }, 2000);
      } else {
        setError('Respuesta inválida del servidor');
        setVerified(false);
      }
    } catch (error) {
      console.error('Verification error:', error);
      console.error('Error response:', error.response?.data);
      
      // If token not found, it might have been already used/verified
      if (error.response?.data?.error === 'INVALID_TOKEN' || error.response?.data?.error === 'TOKEN_EXPIRED') {
        // Check if it says already verified
        if (error.response?.data?.message?.includes('ya verificado') || error.response?.data?.alreadyVerified) {
          setVerified(true);
          setError('');
          toast.success('Tu correo ya fue verificado. Redirigiendo al login...');
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: 'Tu correo está verificado. Puedes iniciar sesión.',
                verified: true
              }
            });
          }, 1500);
        } else {
          setError(error.response?.data?.message || 'Token de verificación inválido o expirado');
          setVerified(false);
        }
      } else {
        setError(error.response?.data?.message || 'Error al verificar el correo');
        setVerified(false);
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card verify-email-card">
          <div className="auth-header">
            <Logo size="large" showText={true} />
            <h1 className="auth-title">Verificación de Correo</h1>
          </div>

          <div className="verify-content">
            {verifying && (
              <div className="verify-status verifying">
                <FiLoader className="status-icon spinning" />
                <h2>Verificando tu correo...</h2>
                <p>Por favor espera un momento</p>
              </div>
            )}

            {!verifying && verified && (
              <div className="verify-status success">
                <FiCheckCircle className="status-icon" />
                <h2>¡Correo Verificado!</h2>
                <p>Tu cuenta ha sido verificada exitosamente.</p>
                <p>Serás redirigido automáticamente...</p>
                <Link to="/" className="verify-button">
                  Ir al Inicio
                </Link>
              </div>
            )}

            {!verifying && !verified && (
              <div className="verify-status error">
                <FiXCircle className="status-icon" />
                <h2>Error en la Verificación</h2>
                <p>{error}</p>
                <div className="verify-actions">
                  <Link to="/resend-verification" className="verify-button">
                    Reenviar Correo
                  </Link>
                  <Link to="/login" className="verify-button secondary">
                    Ir al Login
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
